import type { JSX } from 'react';
import { useCallback, useRef } from 'react';

import { Button, Text } from '@change/ds-core-ui';

import { Translate } from '@change-corgi/core/react/i18n';

import { useMarkModuleAsLoaded } from 'src/app/pages/starterDashboardV2/shared/contexts';
import { Waypoint } from 'src/app/shared/components/scroll';

import { CardCarousel } from './components/CardCarousel';
import { QRPosterCard } from './components/QRPoster/components/QRPosterCard/variant2';
import { ReadyToSharePostsCard } from './components/ReadyToSharePostsCard/variant2';
import { RecommendedGroupsCard } from './components/RecommendedGroupsCard/variant2';
import { SocialContentCard } from './components/SocialContent/components/SocialContentCard/variant2';
import { useSharingToolsTracking } from './hooks';
import { useSharingTools } from './hooks/useSharingTools/variant2';
import { useSharingToolsOrder } from './hooks/useSharingToolsOrder';

export function Variant2(): JSX.Element {
	const { data, actions } = useSharingTools();
	const { trackView } = useSharingToolsTracking('petition_dashboard_grow_petition_v2');

	useMarkModuleAsLoaded('SharingTools', true);

	const hasRecommendedGroups = data.recommendedGroups.isLoading || data.recommendedGroups.groups.length > 0;
	const { orderedKeys, initialIndex, registerClick } = useSharingToolsOrder(hasRecommendedGroups);

	const components: Record<string, JSX.Element> = {
		'recommended-groups': <RecommendedGroupsCard />,
		'ready-to-share-posts': <ReadyToSharePostsCard />,
		'social-content': <SocialContentCard />,
		'qr-poster': <QRPosterCard />,
	};

	const items = orderedKeys.map((key) => {
		const component = components[key];
		return {
			key,
			component,
		};
	});

	const isInitialMount = useRef(true);
	const handleActiveIndexChange = useCallback(
		(activeIndex: number) => {
			// Skip on initial mount - only track when user navigates
			if (isInitialMount.current) {
				isInitialMount.current = false;
				return;
			}

			// Map the activeIndex to the correct key (accounting for loop clones)
			// The carousel's exposedActiveIndex already accounts for clones
			if (activeIndex >= 0 && activeIndex < orderedKeys.length) {
				const activeKey = orderedKeys[activeIndex];
				registerClick(activeKey);
			}
		},
		[orderedKeys, registerClick],
	);

	return (
		<>
			<Waypoint onPositionChange={({ currentPosition }) => trackView(currentPosition)} />
			<div className="flex w-full flex-col gap-5 overflow-x-visible py-8 md:px-12 md:pt-8 md:pb-12">
				<div className="flex flex-row items-center justify-between px-4 md:px-0">
					<Text variant="heading" size="small">
						<Translate value="corgi.petition.starter_dashboard_v2.sharing_tools.title" />
					</Text>
					<Button
						className="px-0 md:px-4"
						data-qa="all-tools-button"
						variant="tertiary"
						size="small"
						onClick={actions.navigateToAllTools}
					>
						<Translate value="corgi.petition.starter_dashboard_v2.sharing_tools.all_tools" />
					</Button>
				</div>
				<CardCarousel
					items={items}
					trackingContext="petition_dashboard_grow_petition_v2"
					initialIndex={initialIndex}
					onActiveIndexChange={handleActiveIndexChange}
				/>
			</div>
		</>
	);
}
